import React from 'react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';

import { Toolbar, ToolbarItemPosition, ToolbarItemsSet } from 'controls/react/layout/toolbar';
import { Section } from 'controls/react/layout/section';
import { AntModal } from 'controls/react/ant/antModal';
import { Grid } from 'controls/grid/grid';
import { AntRadioGroup } from 'controls/react/ant/antRadio';
import { AntSwitch } from 'controls/react/ant/antSwitch';
import { linkModel, MobxManager } from 'framework/mobx-integration';
import { GridSearchInput } from 'controls/grid/gridSearchInput';
import { getDataRegistrySelectedIds } from 'areas/management/data-registry/api';
import { DataSizeIndicator } from 'controls/grid/dataSizeIndicator';
import { apiFetch } from 'framework/api';
import { ViewSelector } from 'controls/grid/viewManager/viewSelector';
import { i18n } from './localization';
import { ActionButtons } from 'controls/react/form';
import { AntCheckbox } from 'controls/react/ant/antCheckbox';
import { GridFilteredIndicator } from 'controls/grid/gridFilteredIndicator';
import { DataRegistryStore } from 'areas/management/data-registry/dataRegistryStore';
import { useStore } from 'core/react/useStore';

export const DataRegistryWindow = observer((props: DataRegistryWindowStoreProps) => {
	const store = useStore(() => {
		return new DataRegistryWindowStore(props);
	});

	const gridStore = store.dataRegistryStore?.gridStore;

	if (!gridStore) return null;

	const footer = (
		<Toolbar appearance={'transparent'}>
			<div position={ToolbarItemPosition.AFTER_TITLE}></div>
			<AntCheckbox
				position={ToolbarItemPosition.AFTER_TITLE}
				{...linkModel(store.dataRegistryStore, 'showOnlySelected')}
			>
				{i18n('Show Selected metrics')}
			</AntCheckbox>
			<ActionButtons mode={'update'} onSave={store.save} onCancel={store.cancel} />
		</Toolbar>
	);

	return (
		<AntModal
			width={1200}
			height={600}
			open={true}
			mode={'update'}
			onOk={store.save}
			footer={footer}
			onCancel={store.cancel}
			mask={true}
		>
			<Section contentPadding={false} height={'fit'}>
				<Toolbar title={'Metrics'}>
					<ToolbarItemsSet position={ToolbarItemPosition.AFTER_TITLE}>
						<div>{i18n('Categorize by')}</div>
						<AntRadioGroup
							options={store.dataRegistryStore.layouts}
							optionType={'button'}
							buttonStyle={'solid'}
							size={'small'}
							{...linkModel(store.dataRegistryStore, 'selectedLayout')}
						/>
					</ToolbarItemsSet>
					<AntSwitch
						position={ToolbarItemPosition.AFTER_TITLE}
						{...linkModel(store.dataRegistryStore, 'includeSubaccounts')}
						size={'small'}
						checkedChildren={i18n('Include subaccounts')}
						unCheckedChildren={i18n('Include subaccounts')}
					/>
					<GridFilteredIndicator store={gridStore} position={ToolbarItemPosition.AFTER_TITLE} />
					<DataSizeIndicator store={gridStore} />
					<GridSearchInput store={gridStore} />
					<ViewSelector store={gridStore} createViewDisabled={true} />
				</Toolbar>
				<Grid store={gridStore} />
			</Section>
		</AntModal>
	);
});

type DataRegistryWindowStoreProps = {
	accountId: string;
	defaultSelectedMetricIds: string[];
	limit?: number;
	onAdd: (ids: string[]) => void;
	onClose: () => void;
	singleMetricSelection?: boolean;
	userSettingsKey?: string;
};

export class DataRegistryWindowStore {
	dataRegistryStore: DataRegistryStore;

	mobx = new MobxManager();
	constructor(public props: DataRegistryWindowStoreProps) {
		makeAutoObservable(this);
		this.mobx.reaction(
			() => this.props.defaultSelectedMetricIds,
			() => {
				this.dataRegistryStore?.destroy();
				this.init();
			},
		);
	}

	init() {
		this.dataRegistryStore = new DataRegistryStore(this.props);
		this.dataRegistryStore.initDefault({
			userSettingsPrefix: 'dashboard',
		});
	}

	save = async () => {
		const idsResponse = await apiFetch(getDataRegistrySelectedIds(this.dataRegistryStore.gridStore));

		if (idsResponse.success) {
			this.props.onAdd(idsResponse.data);
		}
	};

	cancel = () => {
		this.props.onClose();
	};

	destroy = () => {
		this.dataRegistryStore.destroy();
		this.mobx.destroy();
	};
}
